import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const scrollToId = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className="flex items-center rounded-full border border-slate-300 bg-white/85 backdrop-blur p-0.5 shadow-sm">
      <Languages className="w-3.5 h-3.5 text-slate-400 ml-2 mr-1" />
      {["en", "hi"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 text-[11px] font-bold rounded-full transition-colors duration-200 ${
            lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {l === "en" ? "EN" : "हिंदी"}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  const LINKS = [
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.mission"), href: "#mission" },
    { label: t("nav.whatWeDo"), href: "#what-we-do" },
    { label: t("nav.roadmap"), href: "#roadmap" },
    { label: t("nav.nqm"), href: "#nqm" },
    { label: t("nav.news"), href: "#news" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.12)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => go(e, "#top")}
          className="flex flex-col items-center group"
        >
          <img
            src="/qsafe-logo.png"
            alt="QSafe India — Trust for the Quantum Era"
            className="h-10 w-auto rounded-lg shadow-[0_8px_20px_-8px_rgba(15,23,42,0.45)] group-hover:scale-[1.03] transition-transform duration-300"
          />
          <span className="mt-1 font-mono text-[8px] font-bold tracking-[0.32em] uppercase text-slate-500">
            Quantum Safe India
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={(e) => go(e, l.href)}
              className="text-sm font-semibold text-slate-600 hover:text-blue-700 transition-colors duration-200 relative after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-blue-700 hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="nav-procurement-cta"
            onClick={(e) => go(e, "#contact")}
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-white bg-slate-900 hover:bg-blue-700 px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            {t("nav.demo")}
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden grid place-items-center w-10 h-10 rounded-lg border border-slate-200 bg-white text-slate-700"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            aria-controls="mobile-nav-menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-nav-menu"
            aria-label="Mobile navigation"
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  className="text-base font-semibold text-slate-700"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
